import { Button, Stack, TextField, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import { useMemo } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useMutation } from 'urql'
import { graphql } from '../../gql'
import { CreatePurchaseOrderInput } from '../../gql/graphql'
import { currencyFormatter } from '../../utils/currencyFormatter'

const createMutation = graphql(/* GraphQL */ `
    mutation AddPOFormMuation($input: CreatePurchaseOrderInput!) {
      CreatePurchaseOrder(input: $input) {
        id
      }
    }
`)

type TProps = {
  quoteId: number
  onPOCreated: () => void
  defaultCommission?: number
}
export default function AddPOForm(props: TProps) {
  const { quoteId, onPOCreated, defaultCommission = 2.5 } = props

  const [createPOResult, createPO] = useMutation(createMutation)

  /**
   * Submit the mutation to create the PO
   */
  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    if (createPOResult.fetching) return
    const amount = Number.parseFloat(data.amount.replace(/[^0-9.]/g, ''))
    if (Number.isNaN(amount)) {
      alert('Amount must be a number')
      return
    }
    if (!data.commission) {
      alert('Commission is required')
      return
    }
    if (Number.isNaN(Number(data.commission))) {
      alert('Commission must be a number')
      return
    }
    const input: CreatePurchaseOrderInput = {
      quoteId,
      amount,
      commissionPercentage: Number(data.commission),
      poNumber: String(data.poNumber),
      confirmationNumber: data.confirmationNumber,
    }
    if (data.dateReceived) {
      input.dateReceived = data.dateReceived.toISOString()
    }
    if (data.dateRequestedShipment) {
      input.dateRequestedShipment = data.dateRequestedShipment.toISOString()
    }
    if (data.dateEstimatedShipment) {
      input.dateEstimatedShipment = data.dateEstimatedShipment.toISOString()
    }
    const res = await createPO({
      input,
    })
    if (res.error) {
      console.error(res.error)
      alert('Error creating purchase order')
    } else {
      onPOCreated()
    }
  }

  interface IFormInput {
    amount: string
    commission: number
    confirmationNumber: string
    poNumber: string
    dateReceived: Dayjs | null
    dateRequestedShipment?: Dayjs | null
    dateEstimatedShipment?: Dayjs | null
  }

  const { handleSubmit, control, formState, watch, setValue } = useForm<IFormInput>({
    defaultValues: {
      amount: '',
      commission: defaultCommission,
      confirmationNumber: '',
      poNumber: '',
      dateReceived: dayjs(),
      dateRequestedShipment: null,
      dateEstimatedShipment: null,
    },
    mode: 'onBlur',
    disabled: createPOResult.fetching,
  })

  const commission = watch('commission')
  const amount = watch('amount')

  const handleAmountBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const v = e.target.value
    const n = Number.parseFloat(v.replace(/[^0-9.]/g, ''))
    if (!Number.isNaN(n)) {
      let parts = currencyFormatter.formatToParts(n)
      if (parts.length === 0) return
      if (parts.find((p) => p.type === 'fraction')?.value === '00') {
        parts = parts.filter((p) => !['fraction', 'decimal'].includes(p.type))
      }
      setValue(
        'amount',
        parts
          .filter((p) => p.type !== 'currency')
          .map((p) => p.value)
          .join(''),
      )
    }
  }

  const handleCommissionBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const v = e.target.value
    if (typeof v !== 'string') return
    const n = Number.parseFloat(v.replace(/[^0-9.]/g, ''))
    if (!Number.isNaN(n)) {
      setValue('commission', n)
    }
  }

  const estimatedIncome = useMemo<number | undefined>(() => {
    if (!amount || !commission) return undefined
    const n = Number.parseFloat(amount.replace(/[^0-9.]/g, ''))
    if (Number.isNaN(n) || Number.isNaN(commission)) return undefined
    return n * (commission / 100)
  }, [commission, amount])

  const $estimatedIncome = useMemo(() => {
    if (!estimatedIncome) return null
    const formatted = (
      <Typography component="span" color="success" fontSize="inherit" fontWeight="bold">
        {currencyFormatter.format(estimatedIncome)}
      </Typography>
    )

    if (estimatedIncome > 500) {
      return (
        <Typography variant="caption" color="textDisabled">
          Wowzas! You've got {formatted} coming your way.
        </Typography>
      )
    }
    if (estimatedIncome > 50) {
      return (
        <Typography variant="caption" color="textDisabled">
          Nice! This will net you {formatted}!
        </Typography>
      )
    }
  }, [estimatedIncome])

  return (
    <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
      <Stack sx={{ ml: 0, mt: 2 }} spacing={4} direction="column" justifyContent="stretch">
        <Controller
          name="amount"
          control={control}
          rules={{
            required: true,
            validate: (v) => {
              if (!v) return 'Amount is required'
              const n = Number.parseFloat(v as any as string)
              if (Number.isNaN(n)) {
                return 'Amount must be a number'
              }
              return true
            },
            onBlur: handleAmountBlur,
          }}
          render={({ field, fieldState }) => {
            return (
              <TextField
                label="Amount ($) *"
                {...field}
                error={!!fieldState.error}
                helperText={fieldState.error?.message || 'The total amount of the purchase order'}
                slotProps={{
                  input: {
                    startAdornment: '$',
                    required: true,
                  },
                }}
              />
            )
          }}
        />

        <Controller
          name="poNumber"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <TextField
                label="PO Number"
                {...field}
                error={!!fieldState.error}
                helperText={fieldState.error?.message || undefined}
              />
            )
          }}
        />

        <Controller
          name="commission"
          control={control}
          rules={{
            required: true,
            validate: (v) => {
              if (!v) return 'Field is required'
              const n = Number.parseFloat(v as any as string)
              if (Number.isNaN(n)) {
                return 'Must be a number'
              }
              return true
            },
            onBlur: handleCommissionBlur,
          }}
          render={({ field, fieldState }) => {
            return (
              <TextField
                label="Commission (%) *"
                {...field}
                error={!!fieldState.error}
                helperText={fieldState.error?.message || 'Your commission percentage'}
                slotProps={{
                  input: {
                    endAdornment: '%',
                    required: true,
                  },
                }}
              />
            )
          }}
        />

        <Controller
          name="confirmationNumber"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <TextField
                label="Confirmation Number"
                {...field}
                error={!!fieldState.error}
                helperText={fieldState.error?.message || undefined}
              />
            )
          }}
        />

        <Controller
          name="dateReceived"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <DatePicker
                label="Date Received"
                {...field}
                disableFuture
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message || 'The date you received the purchase order',
                  },
                  field: {
                    clearable: true,
                  },
                }}
              />
            )
          }}
        />

        <Controller
          name="dateRequestedShipment"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <DatePicker
                label="Requested Shipment Date"
                {...field}
                // disabled={fetching}
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message || 'The date your client requested the shipment',
                  },
                  field: {
                    clearable: true,
                  },
                }}
              />
            )
          }}
        />

        <Controller
          name="dateEstimatedShipment"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <DatePicker
                label="Estimated Shipment Date"
                {...field}
                // disabled={fetching}
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message || 'When your vendor estimates the shipment will be sent',
                  },
                  field: {
                    clearable: true,
                  },
                }}
              />
            )
          }}
        />

        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
          {$estimatedIncome}
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            disabled={createPOResult.fetching || !formState.isValid}
          >
            Submit
          </Button>
        </Stack>
      </Stack>
    </form>
  )
}
